import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useT } from "@/lib/i18n";
import { AlertTriangle, ArrowRight, ArrowUpRight, ChevronDown, Radio } from "lucide-react";

export type NewsItem = {
  id: number;
  title: string;
  description: string | null;
  url: string | null;
  source: string | null;
  published_at: string;
  importance_score: number | null;
  is_critical: boolean;
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const INITIAL_VISIBLE = 6;
const ARCHIVE_THRESHOLD = 24;

export default function NewsSection() {
  const { t } = useT();
  const [items, setItems] = useState<NewsItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, count } = await supabase
        .from("news")
        .select("id,title,description,url,source,published_at,importance_score,is_critical", { count: "exact" })
        .order("published_at", { ascending: false })
        .limit(ARCHIVE_THRESHOLD);
      if (!cancelled) {
        setItems((data ?? []) as NewsItem[]);
        setTotalCount(count ?? data?.length ?? 0);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleItems = expanded ? items : items.slice(0, INITIAL_VISIBLE);

  return (
    <section
      id="news"
      aria-labelledby="news-heading"
      className="relative border-t border-border bg-gradient-to-b from-background via-hvi-surface/40 to-background py-14 px-4 md:px-8 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-hvi-primary/60 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[80%] h-64 rounded-full blur-3xl opacity-20 bg-hvi-primary"
      />

      <div className="max-w-7xl mx-auto relative">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-hvi-primary mb-2">
              <Radio className="w-3 h-3 animate-pulse" />
              <span>{t("news.subtitle")}</span>
            </div>
            <h2
              id="news-heading"
              className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05] text-foreground"
            >
              {t("news.title")}
            </h2>
          </div>
          <div className="text-[10px] font-data uppercase tracking-wider text-muted-foreground border border-border px-2.5 py-1 rounded-full">
            {totalCount} stories
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground py-16 text-center border border-dashed border-border rounded-xl">
            {t("news.loading")}
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-16 text-center border border-dashed border-border rounded-xl">
            {t("news.empty")}
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {visibleItems.map((n, i) => (
                <NewsCard
                  key={n.id}
                  item={n}
                  variant={i === 0 ? "lead" : "standard"}
                />
              ))}
            </div>

            {items.length > INITIAL_VISIBLE && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="group inline-flex items-center gap-2 px-5 py-2.5 border border-border text-foreground/80 text-[11px] uppercase tracking-[0.18em] font-bold hover:border-hvi-primary/60 hover:text-hvi-primary transition-colors rounded-sm"
                >
                  {expanded ? `Show less` : `Show ${items.length - INITIAL_VISIBLE} more`}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
            )}

            {totalCount > items.length && (
              <div className="mt-4 flex justify-center">
                <Link
                  to="/news"
                  className="group inline-flex items-center gap-2 px-5 py-2.5 border border-hvi-primary/40 text-hvi-primary text-[11px] uppercase tracking-[0.18em] font-bold hover:bg-hvi-primary/10 transition-colors rounded-sm"
                >
                  View full archive ({totalCount})
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export function NewsCard({
  item,
  variant,
}: {
  item: NewsItem;
  variant: "lead" | "standard";
}) {
  const isLead = variant === "lead";
  const safeUrl = item.url && /^https:\/\//i.test(item.url) ? item.url : null;

  const inner = (
    <article
      className={[
        "group relative h-full flex flex-col justify-between overflow-hidden",
        "border bg-card/60 backdrop-blur-sm transition-all duration-300",
        "hover:bg-card hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(0,0,0,0.5)]",
        item.is_critical
          ? "border-destructive/50 hover:border-destructive"
          : "border-border hover:border-hvi-primary/60",
        isLead ? "p-5 md:p-7 min-h-[280px] lg:min-h-full" : "p-4 md:p-5",
      ].join(" ")}
    >
      {/* Critical accent stripe */}
      {item.is_critical && (
        <span
          aria-hidden
          className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-destructive via-destructive/70 to-transparent"
        />
      )}

      {/* Top row: source + meta */}
      <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider mb-3">
        <span
          className={[
            "inline-flex items-center gap-1.5 font-bold px-2 py-0.5 rounded-sm",
            item.is_critical
              ? "bg-destructive/15 text-destructive"
              : "bg-hvi-primary/10 text-hvi-primary",
          ].join(" ")}
        >
          {item.is_critical && <AlertTriangle className="w-2.5 h-2.5" />}
          {item.source ?? "—"}
        </span>
        <span className="text-muted-foreground font-data flex items-center gap-2 flex-wrap justify-end">
          <time dateTime={item.published_at} title={new Date(item.published_at).toLocaleString()}>
            {formatDate(item.published_at)}
          </time>
          <span className="opacity-30">·</span>
          <span className="normal-case tracking-normal">{timeAgo(item.published_at)}</span>
          {typeof item.importance_score === "number" && (
            <>
              <span className="opacity-30">·</span>
              <span
                title="Editorial priority score (0–10): how newsworthy this story is for the outbreak"
                className="px-1.5 py-0.5 rounded-sm bg-muted/50 text-foreground/70"
              >
                Priority {item.importance_score}/10
              </span>
            </>
          )}
        </span>
      </div>

      {/* Title */}
      <h3
        className={[
          "font-semibold tracking-tight leading-tight text-foreground",
          "group-hover:text-hvi-primary transition-colors",
          isLead
            ? "text-2xl md:text-3xl line-clamp-4 mb-3"
            : "text-base md:text-lg line-clamp-3 mb-2",
        ].join(" ")}
      >
        {item.title}
      </h3>

      {/* Description */}
      {item.description && (
        <p
          className={[
            "text-muted-foreground leading-relaxed",
            isLead ? "text-sm md:text-base line-clamp-4 mb-4" : "text-[12.5px] line-clamp-3 mb-3",
          ].join(" ")}
        >
          {item.description}
        </p>
      )}

      {/* CTA */}
      {safeUrl && (
        <div
          className={[
            "mt-auto pt-3 flex items-center justify-between border-t border-border/60",
            "text-[11px] uppercase tracking-wider font-bold",
          ].join(" ")}
        >
          <span className="text-foreground/80 group-hover:text-hvi-primary transition-colors">
            Read full report
          </span>
          <ArrowUpRight
            className="w-4 h-4 text-muted-foreground group-hover:text-hvi-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
            aria-hidden
          />
        </div>
      )}
    </article>
  );

  if (safeUrl) {
    return (
      <a
        href={safeUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={item.title}
        className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-hvi-primary rounded-sm"
      >
        {inner}
      </a>
    );
  }
  return <div className="h-full">{inner}</div>;
}
